import React, { useState } from 'react';
import styles from './MobileProjectTableRow.module.css';
import classNames from 'classnames';
import { useAnimatedToggle } from '@shared/Hooks/Ui/useMobileAnimatedToggle';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    notifyErrorCustom,
    notifySuccessCustom,
} from '@shared/Widgets/ToastNotifications/ToastNotifications';
import { FormatDate } from '@shared/Helpers/FormatValue/FormatDate/FormatDate';
import { DeleteProjectWarningModal } from '@modules/BillOfMaterials/Shared/Widgets/Modals/DeleteProjectWarningModal/DeleteProjectWarningModal';
import BasicButton from '@shared/Ui/Buttons/BasicButton/BasicButton';
import { CustomProjectEntity } from '@entities/CustomProject/Types';
import { StepsViewModeEnum } from '@shared/Enums/StepsViewModeEnum';
import { ROUTES } from '@configs/AppRoutes';
import DeleteButton from '@shared/Ui/Buttons/DeleteButton/DeleteButton';
import DropdownButton from '@shared/Ui/Buttons/DropdownButton/DropdownButton';
import { useProjectsListViewModel } from '@viewModels/ProjectsListViewModel/ProjectsListViewModel';

interface MobileProjectTableRow {
    project: CustomProjectEntity;
}

export const MobileProjectTableRow: React.FC<MobileProjectTableRow> = ({
    project,
}) => {
    const { isOpen: openBody, toggle, ref, maxHeight } = useAnimatedToggle(100);

    const { deleteCustomProject, fetchCustomProjectsList } =
        useProjectsListViewModel();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const handleOpenModal = () => setOpenDeleteModal(true);
    const handleCloseModal = () => setOpenDeleteModal(false);

    const handleDeleteProject = async () => {
        const currentPage = Number(searchParams.get('page')) || 1;

        const result = await deleteCustomProject(project.id);
        if (result.error) {
            notifyErrorCustom(result.message ?? 'Failed to delete project');
            return;
        }

        notifySuccessCustom('The project has been deleted successfully.');

        await fetchCustomProjectsList(currentPage);

        const { customProjects } = useProjectsListViewModel.getState();

        if (customProjects.pagination.total === 0) {
            navigate(ROUTES.HOME);
            return;
        }

        const maxPage = Math.ceil(
            customProjects.pagination.total / customProjects.pagination.perPage
        );

        if (currentPage > maxPage) {
            const newPage = Math.max(1, maxPage);
            searchParams.set('page', newPage.toString());
            setSearchParams(searchParams);

            await fetchCustomProjectsList(newPage);
        }

        handleCloseModal();
    };

    const handleViewProject = () => {
        navigate(
            ROUTES.STEP1.getPath(StepsViewModeEnum.PROJECT, String(project.id))
        );
    };

    return (
        <div className={styles.row}>
            <div className={styles.header}>
                <p className={styles.title}>{project.title}</p>
                <div className={styles.actions}>
                    <DeleteButton
                        onClick={handleOpenModal}
                        extraClassName={styles.deleteBtn}
                    />

                    <DropdownButton onClick={toggle} isOpen={openBody} />
                </div>
            </div>

            <div
                ref={ref}
                className={classNames(styles.dynamicContentBlock, {
                    [styles.openBody]: openBody,
                    [styles.closedBody]: !openBody,
                })}
                style={{ maxHeight }}
            >
                <div className={styles.infoBlock}>
                    <p className={styles.blockName}>Creation Date:</p>
                    <p className={styles.blockContent}>
                        {project.createdAt
                            ? FormatDate(project.createdAt, {
                                  format: 'DMY',
                                  separator: '.',
                              })
                            : 'No date'}
                    </p>
                </div>
                <div className={styles.infoBlock}>
                    <p className={styles.blockName}>Target Date:</p>
                    <p className={styles.blockContent}>
                        {project.targetDate
                            ? FormatDate(project.targetDate, {
                                  format: 'DMY',
                                  separator: '.',
                              })
                            : 'No date'}
                    </p>
                </div>
                <div className={styles.infoBlock}>
                    <p className={styles.blockName}>Address:</p>
                    <p
                        className={classNames(
                            styles.address,
                            styles.blockContent
                        )}
                    >
                        {project.address?.fullAddress ?? 'No address'}
                    </p>
                </div>

                <BasicButton
                    onClick={handleViewProject}
                    extraClassName={styles.viewButton}
                >
                    View Project
                </BasicButton>
            </div>
            {openDeleteModal && (
                <DeleteProjectWarningModal
                    projectTitle={project.title}
                    onConfirm={handleDeleteProject}
                    onClose={handleCloseModal}
                />
            )}
        </div>
    );
};
